import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEye, FiEyeOff, FiCopy, FiDownload } from 'react-icons/fi';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useToaster } from '../../../contexts/ToasterContext';
import { ENV } from "../../../utils/env.js";
import AuthLayout from '../../../components/Layout/AuthLayout';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../../components/ui/dialog';

// Enhanced password validation schema
const passwordValidationSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email address').required('Email is required'),
  new_password: Yup.string()
    .min(16, 'Password must be at least 16 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]+$/,
      'Password must contain at least one uppercase letter, one lowercase letter, one digit, and one special character (!@#$%^&*)'
    )
    .test(
      'no-consecutive-chars',
      'Password cannot have more than 3 consecutive repeated characters',
      function(value) {
        if (!value) return true;
        const consecutivePattern = /(.)\1{3,}/;
        return !consecutivePattern.test(value);
      }
    )
    .required('New password is required'),
  backup_code: Yup.string()
    .required('Backup code is required')
    .length(32, 'Backup code must be exactly 32 characters')
});

const ForgotPassword = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);
  const [backupCodesDownloaded, setBackupCodesDownloaded] = useState(false);
  const navigate = useNavigate();
  const { showToast } = useToaster();

  const copyBackupCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      showToast('Backup code copied to clipboard', 'success');
    } catch (error) {
      showToast('Failed to copy backup code', 'error');
    }
  };

  const downloadBackupCodes = () => {
    const content = backupCodes.join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'backup-codes.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    setBackupCodesDownloaded(true);
    showToast('Backup codes downloaded successfully', 'success');
  };

  const proceedToLogin = () => {
    if (!backupCodesDownloaded) {
      showToast('Please download your backup codes before proceeding', 'error');
      return;
    }
    setShowBackupCodes(false);
    navigate('/login');
  };

  const initialValues = {
    email: '',
    new_password: '',
    backup_code: ''
  };

  const handlePasswordChange = async (values, { setSubmitting }) => {
    try {
      const response = await fetch(`${ENV.REACT_APP_URL}api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: values.email,
          backup_code: values.backup_code,
          new_password: values.new_password
        })
      });

      if (response.ok) {
        const responseData = await response.json();
        showToast('Password reset successfully!', 'success');
        setBackupCodes(responseData.backup_codes || []);
        setShowBackupCodes(true);
        setBackupCodesDownloaded(false);
      } else {
        const errorData = await response.json();
        showToast(errorData.message || 'Failed to reset password', 'error');
      }
    } catch (error) {
      showToast('Network error. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <AuthLayout>
        <div className="flex flex-col gap-4 items-center justify-center w-full">
          <div>
            <img src="/logo.svg" alt="StackGuard Logo" className="h-[100px] w-[300px]" />
          </div>
          <div className="text-center">
            <h1 className="text-3xl font-bold">Change Password</h1>
            <p className="text-xl max-w-md mt-4">
              Enter your new password and backup code to change your password.
            </p>
          </div>

          <Formik
            initialValues={initialValues}
            validationSchema={passwordValidationSchema}
            onSubmit={handlePasswordChange}
          >
            {({ isSubmitting, errors, touched, values }) => (
              <Form className="grid grid-cols-2 gap-4 w-full md:px-2">
                <div className="col-span-2">
                  <Field name="email">
                    {({ field }) => (
                      <Input
                        {...field}
                        type="email"
                        placeholder="Enter your email address"
                        disabled={isSubmitting}
                        className="w-full"
                      />
                    )}
                  </Field>
                  <ErrorMessage name="email">
                    {(msg) => (
                      <div className="text-red-400 text-xs mt-1">{msg}</div>
                    )}
                  </ErrorMessage>
                </div>
                
                <div className="col-span-2">
                  <div className="relative">
                    <Field name="new_password">
                      {({ field }) => (
                        <Input
                          {...field}
                          type={showPassword ? "text" : "password"}
                          placeholder="Enter new password"
                          className="pr-10 w-full"
                          disabled={isSubmitting}
                        />
                      )}
                    </Field>
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <FiEyeOff className="h-4 w-4" />
                      ) : (
                        <FiEye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  <ErrorMessage name="new_password">
                    {(msg) => (
                      <div className="text-red-400 text-xs mt-1">{msg}</div>
                    )}
                  </ErrorMessage>
                </div>

                <div className="col-span-2">
                  <Field name="backup_code">
                    {({ field }) => (
                      <Input
                        {...field}
                        type="text"
                        placeholder="Enter your 32-character backup code"
                        disabled={isSubmitting}
                        className="w-full"
                      />
                    )}
                  </Field>
                  <ErrorMessage name="backup_code">
                    {(msg) => (
                      <div className="text-red-400 text-xs mt-1">{msg}</div>
                    )}
                  </ErrorMessage>
                </div>

                {/* Password Requirements */}
                <div className="col-span-2 mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs font-semibold text-blue-800 mb-1">Password Requirements:</p>
                  <ul className="text-xs text-blue-700 space-y-1">
                    <li>• Minimum 16 characters</li>
                    <li>• At least one uppercase letter (A-Z)</li>
                    <li>• At least one lowercase letter (a-z)</li>
                    <li>• At least one digit (0-9)</li>
                    <li>• At least one special character (!@#$%^&*)</li>
                    <li>• No more than 3 consecutive repeated characters</li>
                  </ul>
                </div>

                <button
                  type="submit"
                  className={`col-span-2 text-white py-4 rounded-md font-semibold transition-colors ${
                    values.email && values.new_password && values.backup_code 
                      ? 'bg-[#44087D] hover:bg-[#350661]' 
                      : 'bg-[#454545] hover:bg-[#333333]'
                  }`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Changing Password' : 'Change Password'}
                </button>

               
              </Form>
            )}
          </Formik>
          <div>
            <p className='text-lg'>
              Remembered your password?{' '}
              <button
                onClick={() => navigate('/login')}
                className="underline cursor-pointer text-[#44087D]"
              >
                Login
              </button>
            </p>
          </div>
        </div>
      </AuthLayout>

      <Dialog open={showBackupCodes} onOpenChange={() => {}}>
        <DialogContent className="max-w-2xl bg-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center text-[#200A57]">
              Save Your New Backup Codes
            </DialogTitle>
            <DialogDescription className="text-center text-[#8D82AA]">
              Your password has been reset successfully! These new backup codes will allow you to recover your account. Store them in a safe place - you won't see them again!
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm text-[#200A57]">
                  New Backup Codes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-2">
                  {backupCodes.map((code, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-[#F4F0FD] rounded-lg border border-[#C0ACF3]"
                    >
                      <code className="text-sm font-mono text-[#200A57] flex-1">{code}</code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyBackupCode(code)}
                        className="ml-2 text-[#8D82AA] hover:text-[#200A57]"
                      >
                        <FiCopy size={16} />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <div className="text-yellow-600 mt-1">⚠️</div>
                <div>
                  <p className="text-sm font-semibold text-yellow-800 mb-1">
                    Important Security Notice
                  </p>
                  <ul className="text-xs text-yellow-700 space-y-1">
                    <li>• These backup codes can only be used once each</li>
                    <li>• Store them securely offline (not in cloud storage)</li>
                    <li>• You must download them before proceeding</li>
                    <li>• Your old backup codes are no longer valid</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button
              onClick={downloadBackupCodes}
              className="bg-[#44087D] hover:bg-[#350661] text-white flex items-center gap-2"
            >
              <FiDownload size={16} />
              Download Backup Codes
            </Button>
            <Button
              onClick={proceedToLogin}
              disabled={!backupCodesDownloaded}
              variant={backupCodesDownloaded ? 'default' : 'outline'}
              className={
                backupCodesDownloaded
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'opacity-50 cursor-not-allowed'
              }
            >
              {backupCodesDownloaded ? 'Proceed to Login' : 'Download Required'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ForgotPassword;
